import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { MotionValue } from 'motion/react'

import { useEffect, useRef } from 'react'

import {
    motion,
    useMotionValue,
    useScroll,
    useSpring,
    useTransform
} from 'motion/react'

import {
    ArrowLeftRight,
    ArrowRight,
    FilePlus2,
    LifeBuoy,
    Sparkles,
    Wrench
} from 'lucide-react'

import { useMediaQuery } from '@/hooks/use-media-query'
import { useReduceMotion } from '@/hooks/use-reduce-motion'
import { arrowClass, buttonClass } from '@/components/generals/button'
import Magnetic from '@/components/generals/magnetic'
import QuoteLink from '@/components/generals/quote-link'
import { existingSite } from '@/constants/content'

interface BrokenCharProps {
    char: string
    index: number
    progress: MotionValue<number>
    spread: MotionValue<number>
    still: boolean
}

const ICONS: Array<LucideIcon> = [Wrench, FilePlus2, ArrowLeftRight, Sparkles, LifeBuoy]

// Deterministic pseudo-random numbers, identical on the server and in the browser.
const jitter = (seed: number): number => ((((seed + 1) * 2654435761) >>> 0) % 1000) / 1000 - 0.5

// A letter that starts knocked out of place and snaps back into line as the heading scrolls into view,
// the way a broken site gets put right.
const BrokenChar: FC<BrokenCharProps> = ({ char, index, progress, spread, still }): ReactNode => {
    const settle = (value: number): number => 1 - Math.min(Math.max(value, 0), 1)
    const rotate = useTransform(progress, value => jitter(index * 3) * 44 * settle(value))
    const x = useTransform(progress, value => `${jitter(index * 3 + 2) * 0.18 * settle(value)}em`)

    // Phones get a smaller vertical scatter, so the two lines never tangle into what looks like three.
    const y = useTransform([progress, spread], ([value, amount]: Array<number>) => `${jitter(index * 3 + 1) * 0.7 * amount * settle(value)}em`)

    return (
        <motion.span
            aria-hidden='true'
            className='inline-block'
            style={still ? undefined : { rotate, y, x }}
        >
            {char}
        </motion.span>
    )
}

const ExistingSite: FC = (): ReactNode => {
    const headingRef = useRef<HTMLHeadingElement>(null)
    const reduce = useReduceMotion()
    const isDesktop = useMediaQuery('(min-width: 1024px)')
    const { scrollYProgress } = useScroll({ target: headingRef, offset: ['start 95%', 'start 45%'] })
    const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 26, mass: 0.4 })
    const spread = useMotionValue(0.35)

    useEffect(() => {
        spread.set(isDesktop ? 1 : 0.35)
    }, [isDesktop, spread])

    let charIndex = 0
    const headingWords = existingSite.heading.split(' ')
    const breakAt = headingWords.indexOf('have') + 1
    const headingLines = breakAt > 0 ? [headingWords.slice(0, breakAt), headingWords.slice(breakAt)] : [headingWords]

    return (
        <section
            id='existing'
            className='relative overflow-x-clip bg-blaze-deep text-paper [--focus-ring:var(--paper)]'
        >
            <div className='shell grid gap-14 py-24 sm:py-32 lg:grid-cols-12 lg:gap-10 lg:py-40'>
                <div className='lg:col-span-5'>
                    <div className='lg:sticky lg:top-28'>
                        <h2
                            ref={headingRef}
                            aria-label={existingSite.heading}
                            className='font-heading text-[13vw] font-bold leading-[0.88] tracking-[-0.015em] wdth-78 sm:text-[10vw] lg:text-[6vw] 2xl:text-[5.6rem]'
                        >
                            {headingLines.map((line, lineIndex) => (
                                <span key={line.join(' ')} className='block whitespace-nowrap lg:inline lg:whitespace-normal'>
                                    {line.map((word, wordIndex) => (
                                        <span key={`${word}-${wordIndex}`}>
                                            <span className='inline-block whitespace-nowrap'>
                                                {word.split('').map((char) => {
                                                    const index = charIndex

                                                    charIndex += 1

                                                    return (
                                                        <BrokenChar
                                                            key={index}
                                                            char={char}
                                                            index={index}
                                                            progress={progress}
                                                            spread={spread}
                                                            still={reduce}
                                                        />
                                                    )
                                                })}
                                            </span>

                                            {wordIndex < line.length - 1 || lineIndex < headingLines.length - 1 ? ' ' : ''}
                                        </span>
                                    ))}
                                </span>
                            ))}
                        </h2>

                        <p className='mt-8 max-w-[26rem] text-xl leading-relaxed sm:text-2xl'>
                            {existingSite.intro}
                        </p>

                        <Magnetic className='mt-10 w-full sm:w-auto'>
                            <QuoteLink
                                path='fix'
                                className={buttonClass({ variant: 'paper', size: 'lg', className: 'w-full' })}
                            >
                                {existingSite.cta}
                                <ArrowRight aria-hidden='true' className={arrowClass} />
                            </QuoteLink>
                        </Magnetic>
                    </div>
                </div>

                <ul className='border-t border-paper/30 lg:col-span-7'>
                    {existingSite.items.map((item, index) => {
                        const Icon = ICONS[index] ?? Wrench

                        return (
                            <motion.li
                                key={item.title}
                                className='group flex gap-5 border-b border-paper/30 py-7 sm:gap-7 sm:py-9'
                                initial={{ x: 40, opacity: 0 }}
                                whileInView={{ x: 0, opacity: 1 }}
                                viewport={{ once: true, amount: 0.5 }}
                                transition={{ duration: 0.7, delay: 0.05 * index, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <span
                                    aria-hidden='true'
                                    className='grid size-12 shrink-0 place-items-center rounded-lg border-2 border-paper/40 transition-[background-color,color,rotate,border-color] duration-300 ease-bounce group-hover:-rotate-6 group-hover:border-paper group-hover:bg-paper group-hover:text-blaze-deep sm:size-14'
                                >
                                    <Icon className='size-6' />
                                </span>

                                <div>
                                    <h3 className='font-heading text-2xl font-extrabold leading-tight tracking-[-0.02em] wdth-85 sm:text-3xl'>
                                        {item.title}
                                    </h3>

                                    <p className='mt-2 max-w-[34rem] text-base leading-relaxed text-paper/90 sm:text-lg'>
                                        {item.body}
                                    </p>
                                </div>
                            </motion.li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}

export default ExistingSite
